// Wire-format status values — the UPPERCASE enum code the API emits. Korean display
// labels resolve from the backend-served label catalog at render time, keyed by these codes.
export type AdminStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'WITHDRAWN';

export const ADMIN_STATUSES: readonly AdminStatus[] = [
  'PENDING',
  'ACTIVE',
  'INACTIVE',
  'WITHDRAWN',
] as const;

export interface InactiveHistoryEntry {
  // Free-text reason captured from `note` at Active-transition time.
  // Server substitutes "(사유 미기재)" if note was empty.
  reason: string;
  from: string;  // ISO (server may emit YYYY.MM.DD legacy — FE formats either way).
  to: string;    // ISO of the Active transition.
}

export interface Admin {
  // v9 (DEC-DB-PK-BIGINT-001): wire id is BIGINT UNSIGNED → number. URL params (React Router)
  // remain strings; parse with Number() at the call site.
  id: number;
  email: string;
  name: string;
  phone: string | null;
  institution: string;
  department: string | null;
  // References Role.name; an account can hold 0..N non-root roles (PRD 15 §3.1, §4.1).
  roles: string[];
  // The Root flag is independent of `roles[]` — Root has full access regardless.
  isRoot: boolean;
  status: AdminStatus;
  joinedAt: string | null;        // ISO
  lastLoginAt: string | null;     // ISO
  passwordChangedAt: string | null;
  // Free-text note (typically the reason for Inactive). Cleared by server on Active transition.
  // Optional on the wire — defensive default for BE rollouts that don't yet expose it.
  note?: string | null;
  // Set when row enters Inactive; cleared on Active. ISO.
  inactiveSince?: string | null;
  // Append-only ledger of past inactive periods. Reverse-chronological at render time.
  inactiveHistory?: InactiveHistoryEntry[];
  // True when `email`/`phone` above were read out of the withdrawal archive rather than off the live row.
  // A withdrawal moves both values away and frees the addresses for reuse, so on a WITHDRAWN row they are
  // what the account USED to hold, not what it holds now — which is why the panel and the list mark them
  // with a 탈퇴 chip instead of showing them as ordinary current values.
  // Optional on the wire — defensive default for BE rollouts that don't yet emit it.
  identityArchived?: boolean;
}

export type AdminSortKey = 'id' | 'name' | 'role' | 'status' | 'lastLoginAt';
export type SortDirection = 'asc' | 'desc';

// Special role-filter sentinel: surfaces only `isRoot=true` rows (PRD 15 §3.1).
export const ROLE_FILTER_ROOT = '__ROOT__';

export interface ListAdminsParams {
  // Role.name OR ROLE_FILTER_ROOT.
  role?: string;
  status?: AdminStatus;
  // Substring match on name OR email (case-insensitive).
  q?: string;
  page?: number;
  perPage?: number;
  sortBy?: AdminSortKey;
  sortDir?: SortDirection;
}

export interface ListAdminsResult {
  items: Admin[];
  total: number;
  page: number;
  perPage: number;
}

export interface InviteAdminInput {
  name: string;
  email: string;
  phone: string | null;
  roles: string[];
}

export interface UpdateAdminInput {
  name?: string;
  phone?: string | null;
  roles?: string[];
  status?: AdminStatus;
  // Free-text reason. Sending '' or null on Active-transition triggers server-side
  // inactiveHistory append + note clear. FE sends current form value as-is.
  note?: string | null;
}

export interface RootTransferInput {
  // v9: wire id is BIGINT UNSIGNED → number (matches contract §3.7).
  targetAdminId: number;
  // Roles the outgoing Root will hold after transfer (0..N).
  keepRoles: string[];
  password: string;
}

export interface RootTransferResult {
  newRoot: Admin;
  previousRoot: Admin;
  forcedLogout: true;
}

export type AdminApiErrorCode =
  | 'EMAIL_DUPLICATE'
  | 'EMAIL_INVALID'
  | 'PHONE_INVALID'
  | 'PHONE_DUPLICATE'
  | 'NAME_INVALID'
  | 'ROLE_REQUIRED'
  | 'ROOT_NOT_EDITABLE'
  | 'ROLE_IN_USE_BY_SELF'
  | 'SELF_STATUS_CHANGE_FORBIDDEN'
  | 'SELF_INACTIVATE_FORBIDDEN'
  | 'SELF_WITHDRAW_FORBIDDEN'
  | 'INVALID_TARGET_STATUS'
  | 'EMAIL_SEND_FAILED'
  | 'TARGET_INVALID'
  | 'PASSWORD_INVALID'
  | 'LOCKED_5MIN'
  | 'NO_CANDIDATES'
  | 'TRANSACTION_FAILED'
  | 'INSUFFICIENT_PERMISSION'
  | 'NOT_FOUND'
  // FE-synthesized when the request never reached the server (offline / DNS / timeout),
  // so callers can show an operation-specific failure instead of a generic unknown error.
  | 'NETWORK'
  | 'UNKNOWN';

// Runtime catalog of the codes above. `mapError` matches the server's `error.code`
// against this set instead of casting it: an unrecognised code (e.g. the BE's
// INTERNAL_ERROR on a 5xx) used to pass the cast untouched and its raw server text
// then reached the user through the `e.message` toast branches (SHCT-685).
export const ADMIN_API_ERROR_CODES: ReadonlySet<string> = new Set<AdminApiErrorCode>([
  'EMAIL_DUPLICATE',
  'EMAIL_INVALID',
  'PHONE_INVALID',
  'PHONE_DUPLICATE',
  'NAME_INVALID',
  'ROLE_REQUIRED',
  'ROOT_NOT_EDITABLE',
  'ROLE_IN_USE_BY_SELF',
  'SELF_STATUS_CHANGE_FORBIDDEN',
  'SELF_INACTIVATE_FORBIDDEN',
  'SELF_WITHDRAW_FORBIDDEN',
  'INVALID_TARGET_STATUS',
  'EMAIL_SEND_FAILED',
  'TARGET_INVALID',
  'PASSWORD_INVALID',
  'LOCKED_5MIN',
  'NO_CANDIDATES',
  'TRANSACTION_FAILED',
  'INSUFFICIENT_PERMISSION',
  'NOT_FOUND',
  'NETWORK',
  'UNKNOWN',
]);

export class AdminApiError extends Error {
  readonly code: AdminApiErrorCode;
  readonly fields?: Record<string, string>;
  // Extra context for transfer flow (attemptsLeft, retryAfterSeconds, etc.).
  readonly meta?: Record<string, unknown>;

  constructor(
    code: AdminApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, AdminApiError.prototype);
  }
}

export interface AdminApi {
  listAdmins(params: ListAdminsParams): Promise<ListAdminsResult>;
  inviteAdmin(input: InviteAdminInput): Promise<Admin>;
  resendInvitation(id: number): Promise<void>;
  updateAdmin(id: number, input: UpdateAdminInput): Promise<Admin>;
  resetPassword(id: number): Promise<void>;
  // PRD 15 §3.5.2: Active && !isRoot admins eligible for Root transfer.
  // Unpaginated — user picks one out of N in a single panel view.
  listRootTransferCandidates(): Promise<Admin[]>;
  rootTransfer(input: RootTransferInput): Promise<RootTransferResult>;
}
